"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RatingExpansionEngine = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const expression_tokens_1 = require("./expression-tokens");
const rating_expansion_types_1 = require("./rating-expansion.types");
class Refusal {
    constructor(reason) {
        this.reason = reason;
    }
}
const UNRECOGNIZED = "This expression form isn't recognized for automatic expansion.";
const SLOT_LABEL = {
    min: 'Minimum',
    typ: 'Typical',
    max: 'Maximum',
};
const SLOT_ORDER = ['min', 'typ', 'max'];
const COMPARISON_SYMBOLS = new Set(['<', '>', '≤', '≥']);
const NUMERICAL_TYPE_ORDER = ['range', 'minimum', 'maximum', 'absolute'];
class RatingExpansionEngine {
    static expand(row, context) {
        if (context.kind === rating_expansion_types_1.RatingsKind.Amr && row.typ.trim() !== '') {
            return RatingExpansionEngine.refuse('An absolute maximum rating has no typical value — clear the typical cell.');
        }
        const targetSets = RatingExpansionEngine.targetSets(row, context);
        if (targetSets instanceof Refusal) {
            return RatingExpansionEngine.refuse(targetSets.reason);
        }
        const conditions = RatingExpansionEngine.conditions(row, context);
        if (conditions instanceof Refusal) {
            return RatingExpansionEngine.refuse(conditions.reason);
        }
        const expanded = [];
        for (const slot of SLOT_ORDER) {
            const text = row[slot].trim();
            if (text === '') {
                continue;
            }
            const result = RatingExpansionEngine.expandSlot(slot, text, row, context);
            if (result instanceof Refusal) {
                return RatingExpansionEngine.refuse(`${SLOT_LABEL[slot]}: ${result.reason}`);
            }
            expanded.push(result);
        }
        RatingExpansionEngine.correctReversedRange(expanded);
        const limitSets = new Map();
        for (const targetSet of targetSets) {
            for (const slot of expanded) {
                for (const term of slot.terms) {
                    const relativeSignalId = term.signalId ?? targetSet.relativeSignalId;
                    if (relativeSignalId === null && row.characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
                        return RatingExpansionEngine.refuse(`${SLOT_LABEL[slot.slot]}: a voltage stated as a plain number needs the row's "measured from" reference.`);
                    }
                    const key = `${JSON.stringify(targetSet.target)}|${relativeSignalId ?? ''}`;
                    const limitSet = limitSets.get(key) ?? {
                        target: targetSet.target,
                        relativeSignalId,
                        floors: [],
                        ceilings: [],
                        typicals: [],
                    };
                    limitSets.set(key, limitSet);
                    const flip = targetSet.inverting && term.signalId === null;
                    const signed = flip ? -term.value : term.value;
                    const limit = { value: signed === 0 ? 0 : signed, inclusive: slot.inclusive !== false };
                    const isFloor = flip ? slot.slot === 'max' : slot.slot === 'min';
                    const isCeiling = flip ? slot.slot === 'min' : slot.slot === 'max';
                    (isFloor ? limitSet.floors
                        : isCeiling ? limitSet.ceilings
                            : limitSet.typicals).push(limit);
                }
            }
        }
        const bounds = [];
        for (const limitSet of limitSets.values()) {
            const numericals = RatingExpansionEngine.fold(limitSet, row.characteristic);
            if (numericals instanceof Refusal) {
                return RatingExpansionEngine.refuse(numericals.reason);
            }
            for (const numerical of numericals) {
                bounds.push(RatingExpansionEngine.bound(row, context, limitSet, numerical, conditions));
            }
        }
        const unique = [...new Map(bounds.map((bound) => [bound.id, bound])).values()];
        return { status: rating_expansion_types_1.ExpansionStatus.Expanded, bounds: RatingExpansionEngine.sort(unique), reason: null };
    }
    static refuse(reason) {
        return { status: rating_expansion_types_1.ExpansionStatus.Unrecognized, bounds: [], reason };
    }
    static targetSets(row, context) {
        if (row.characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Power) {
            if (row.relative !== null) {
                return new Refusal('A power rating is a whole-device limit — it takes no reference.');
            }
            return [{ target: { component: true }, relativeSignalId: null, inverting: false }];
        }
        const members = [...(row.signalIds ?? [])];
        for (const groupId of row.groupIds) {
            const group = context.signalGroups.find((candidate) => candidate.id === groupId);
            if (group === undefined) {
                return new Refusal(`The row applies to a signal group the table doesn't declare (${groupId}).`);
            }
            members.push(...group.signalIds);
        }
        const targets = [...new Set(members)].sort();
        if (targets.length === 0) {
            return new Refusal('The row applies to no signals.');
        }
        for (const targetId of targets) {
            if (!RatingExpansionEngine.hasSignal(context, targetId)) {
                return new Refusal(`The row applies to a signal the package doesn't declare (${targetId}).`);
            }
        }
        if (row.characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Current) {
            if (row.relative !== null) {
                return new Refusal('A current rating is a plain number — it takes no reference.');
            }
            return targets.map((targetId) => ({ target: { signal: targetId }, relativeSignalId: null, inverting: false }));
        }
        if (row.relative === null || row.relative.kind === rating_expansion_types_1.RatingsRelativeReferenceKind.Signal) {
            const relativeSignalId = row.relative?.signalId ?? null;
            if (relativeSignalId !== null && !RatingExpansionEngine.hasSignal(context, relativeSignalId)) {
                return new Refusal(`The row's reference names a signal the package doesn't declare (${relativeSignalId}).`);
            }
            return targets.map((targetId) => ({ target: { signal: targetId }, relativeSignalId, inverting: false }));
        }
        const pairs = new Map(row.relative.pairs.map((pair) => [pair.targetId, pair.relativeId]));
        for (const pair of row.relative.pairs) {
            if (!targets.includes(pair.targetId)) {
                return new Refusal(`The reference mapping names a signal the row doesn't apply to (${pair.targetId}).`);
            }
        }
        const sets = [];
        for (const targetId of targets) {
            const relativeId = pairs.get(targetId);
            if (relativeId === undefined || relativeId === null) {
                return new Refusal(`The reference mapping gives ${targetId} nothing to be measured from.`);
            }
            if (!RatingExpansionEngine.hasSignal(context, relativeId)) {
                return new Refusal(`The reference mapping names a signal the package doesn't declare (${relativeId}).`);
            }
            const mutual = pairs.get(relativeId) === targetId;
            const pair = context.pairs.find((candidate) => (candidate.positiveId === targetId && candidate.negativeId === relativeId) ||
                (candidate.positiveId === relativeId && candidate.negativeId === targetId));
            if (mutual && pair === undefined) {
                return new Refusal(`${targetId} and ${relativeId} are measured from each other but aren't declared as a differential pair.`);
            }
            sets.push({
                target: { signal: targetId },
                relativeSignalId: relativeId,
                inverting: mutual && pair !== undefined && pair.negativeId === targetId,
            });
        }
        return sets;
    }
    static conditions(row, context) {
        const ids = [...new Set(row.conditionIds)].sort();
        for (const id of ids) {
            if (!context.conditions.some((condition) => condition.id === id)) {
                return new Refusal(`The row holds under a condition the table doesn't declare (${id}).`);
            }
        }
        return ids.map((mode) => ({ mode, mode_state: true }));
    }
    static hasSignal(context, id) {
        return context.signals.some((signal) => String(signal.id) === id);
    }
    static fold(limitSet, characteristic) {
        const byValue = (a, b) => a.value - b.value;
        const [floor, ...moreFloors] = [...limitSet.floors].sort(byValue);
        const [ceiling, ...moreCeilings] = [...limitSet.ceilings].sort(byValue);
        const [typical, ...moreTypicals] = [...limitSet.typicals].sort(byValue);
        const nominal = typical === undefined ? {} : { nominalValue: typical.value };
        const numericals = [];
        if (floor !== undefined && ceiling !== undefined) {
            if (floor.value > ceiling.value) {
                return new Refusal(`The minimum (${floor.value}) is above the maximum (${ceiling.value}) against the same reference.`);
            }
            numericals.push({
                type: 'range',
                characteristic,
                minVal: floor.value,
                maxVal: ceiling.value,
                ...(floor.inclusive ? {} : { minValIncluded: false }),
                ...(ceiling.inclusive ? {} : { maxValIncluded: false }),
                ...nominal,
            });
        }
        else if (floor !== undefined) {
            numericals.push({
                type: 'minimum',
                characteristic,
                value: floor.value,
                ...(floor.inclusive ? {} : { valInclusive: false }),
                ...nominal,
            });
        }
        else if (ceiling !== undefined) {
            numericals.push({
                type: 'maximum',
                characteristic,
                value: ceiling.value,
                ...(ceiling.inclusive ? {} : { valInclusive: false }),
                ...nominal,
            });
        }
        else if (typical !== undefined) {
            numericals.push({ type: 'absolute', characteristic, value: typical.value });
        }
        for (const extra of moreFloors) {
            numericals.push({
                type: 'minimum',
                characteristic,
                value: extra.value,
                ...(extra.inclusive ? {} : { valInclusive: false }),
            });
        }
        for (const extra of moreCeilings) {
            numericals.push({
                type: 'maximum',
                characteristic,
                value: extra.value,
                ...(extra.inclusive ? {} : { valInclusive: false }),
            });
        }
        for (const extra of moreTypicals) {
            numericals.push({ type: 'absolute', characteristic, value: extra.value });
        }
        return numericals;
    }
    static bound(row, context, limitSet, numerical, conditions) {
        const amr = context.kind === rating_expansion_types_1.RatingsKind.Amr;
        const content = {
            ...(conditions.length === 0 ? { global: true } : { conditions }),
            boundary: {
                type: sigma_engine_shared_1.BoundaryAssertionType.Numerical,
                class: amr ? sigma_engine_shared_1.BoundaryClass.IC_AbsoluteMaximumRating : sigma_engine_shared_1.BoundaryClass.IC_ElectricalCharacteristics,
                level: (0, rating_expansion_types_1.kindDefaultLevel)(context.kind),
                target: limitSet.target,
                ...(limitSet.relativeSignalId === null ? {} : { relative_to: { signal: limitSet.relativeSignalId } }),
                numerical,
            },
        };
        const prefix = amr ? 'amr' : 'iec';
        const identity = `${row.id}|${RatingExpansionEngine.canonicalJson(content)}`;
        const id = `${prefix}_${sigma_engine_shared_1.Utils.fnv1aHash(identity)}${sigma_engine_shared_1.Utils.fnv1aHash(`${identity}|salt`)}`;
        return { id, ...content };
    }
    static canonicalJson(value) {
        if (Array.isArray(value)) {
            return `[${value.map((item) => RatingExpansionEngine.canonicalJson(item)).join(',')}]`;
        }
        if (value !== null && typeof value === 'object') {
            const entries = Object.entries(value)
                .filter(([, item]) => item !== undefined)
                .sort(([a], [b]) => (a < b ? -1 : 1))
                .map(([key, item]) => `${JSON.stringify(key)}:${RatingExpansionEngine.canonicalJson(item)}`);
            return `{${entries.join(',')}}`;
        }
        return JSON.stringify(value);
    }
    static sort(bounds) {
        const key = (bound) => {
            const { boundary } = bound;
            const numerical = boundary.type === sigma_engine_shared_1.BoundaryAssertionType.Numerical ? boundary.numerical : null;
            const relative = boundary.relative_to;
            const reference = relative !== undefined && 'signal' in relative && typeof relative.signal === 'string' ? relative.signal : '';
            return [
                JSON.stringify(boundary.target),
                reference,
                numerical === null ? -1 : NUMERICAL_TYPE_ORDER.indexOf(numerical.type),
                numerical === null ? 0
                    : numerical.type === 'range' ? numerical.minVal
                        : numerical.value,
            ];
        };
        return [...bounds].sort((a, b) => {
            const [targetA, referenceA, typeA, valueA] = key(a);
            const [targetB, referenceB, typeB, valueB] = key(b);
            if (targetA !== targetB) {
                return targetA < targetB ? -1 : 1;
            }
            if (referenceA !== referenceB) {
                return referenceA < referenceB ? -1 : 1;
            }
            if (typeA !== typeB) {
                return typeA - typeB;
            }
            return valueA - valueB;
        });
    }
    static expandSlot(slot, text, row, context) {
        const ingested = (0, expression_tokens_1.ingestExpressionText)(`${text} `, RatingExpansionEngine.resolvableSignals(context));
        if (ingested.error !== null) {
            return new Refusal(ingested.error);
        }
        if (ingested.draft !== '') {
            return new Refusal(`"${ingested.draft}" isn't a complete signal, function, unit, or number.`);
        }
        const structural = (0, expression_tokens_1.validateExpression)(ingested.tokens);
        if (structural !== null) {
            return new Refusal(structural);
        }
        let tokens = RatingExpansionEngine.foldSigns(ingested.tokens);
        if (tokens.some((token) => token.kind === 'operator' && (token.symbol === '×' || token.symbol === '/'))) {
            return new Refusal("Multiplicative limits can't be held as an offset from a reference, and products aren't supported. " +
                'Evaluate it against the nominal rail voltage and enter the result (0.5 × VCC at a nominal 5 V is 2.5 V).');
        }
        let inclusive;
        let statesDirection = false;
        const [subject, comparison] = tokens;
        if (subject?.kind === 'signal' &&
            comparison?.kind === 'operator' &&
            COMPARISON_SYMBOLS.has(comparison.symbol) &&
            RatingExpansionEngine.isTarget(row, context, String(subject.signal.id))) {
            tokens = tokens.slice(1);
        }
        const [first] = tokens;
        if (first?.kind === 'operator' && COMPARISON_SYMBOLS.has(first.symbol)) {
            if (slot === 'typ') {
                return new Refusal("A typical value can't state a comparison.");
            }
            const impliedSlot = first.symbol === '<' || first.symbol === '≤' ? 'max' : 'min';
            if (impliedSlot !== slot) {
                const limit = impliedSlot === 'max' ? 'an upper' : 'a lower';
                const cell = impliedSlot === 'max' ? 'maximum' : 'minimum';
                return new Refusal(`"${first.symbol}" states ${limit} limit, so it belongs in the ${cell} cell.`);
            }
            inclusive = first.symbol === '≤' || first.symbol === '≥' ? undefined : false;
            statesDirection = true;
            tokens = tokens.slice(1);
        }
        if (tokens.some((token) => token.kind === 'operator' && COMPARISON_SYMBOLS.has(token.symbol))) {
            return new Refusal(UNRECOGNIZED);
        }
        const [head] = tokens;
        if (head?.kind !== 'function') {
            const term = RatingExpansionEngine.parseTerm(tokens, row.characteristic);
            if (term instanceof Refusal) {
                return term;
            }
            if (term === null) {
                return new Refusal(UNRECOGNIZED);
            }
            const plain = head?.kind === 'number' && tokens.length <= 2 && !statesDirection;
            return { slot, terms: [term], plain, ...(inclusive === undefined ? {} : { inclusive }) };
        }
        if (head.name !== 'Min' && head.name !== 'Max') {
            return new Refusal('Only Min() and Max() composites can be expanded.');
        }
        if (slot === 'typ') {
            return new Refusal("A typical value can't hold a Min()/Max() composite.");
        }
        if ((head.name === 'Min') !== (slot === 'max')) {
            const boundName = slot === 'min' ? 'a lower' : 'an upper';
            return new Refusal(`${head.name}() on ${boundName} bound is a disjunction — it can't be split into simultaneous bounds.`);
        }
        const terms = RatingExpansionEngine.parseComposite(tokens, head.name, row.characteristic);
        if (terms instanceof Refusal) {
            return terms;
        }
        return { slot, terms, plain: false, ...(inclusive === undefined ? {} : { inclusive }) };
    }
    static resolvableSignals(context) {
        const aliases = context.signals.map((signal) => ({ id: signal.id, name: `V${signal.name}` }));
        return [...context.signals, ...aliases];
    }
    static isTarget(row, context, signalId) {
        return ((row.signalIds ?? []).includes(signalId) ||
            context.signalGroups.some((group) => row.groupIds.includes(group.id) && group.signalIds.includes(signalId)));
    }
    static parseComposite(tokens, name, characteristic) {
        const [, second] = tokens;
        if (!RatingExpansionEngine.isParen(second, '(') || !RatingExpansionEngine.isParen(tokens[tokens.length - 1], ')')) {
            return new Refusal(UNRECOGNIZED);
        }
        let depth = 0;
        for (const [index, token] of tokens.entries()) {
            if (RatingExpansionEngine.isParen(token, '(')) {
                depth += 1;
            }
            if (RatingExpansionEngine.isParen(token, ')')) {
                depth -= 1;
                if (depth === 0 && index !== tokens.length - 1) {
                    return new Refusal(UNRECOGNIZED);
                }
            }
        }
        const terms = [];
        for (const argument of RatingExpansionEngine.splitTopLevelArgs(tokens.slice(2, -1))) {
            const parsed = RatingExpansionEngine.parseArgument(argument, name, characteristic);
            if (parsed instanceof Refusal) {
                return parsed;
            }
            terms.push(...parsed);
        }
        return terms;
    }
    static parseArgument(tokens, outerName, characteristic) {
        const simple = RatingExpansionEngine.parseTerm(tokens, characteristic);
        if (simple instanceof Refusal) {
            return simple;
        }
        if (simple !== null) {
            return [simple];
        }
        const [first, second] = tokens;
        if (first?.kind !== 'function') {
            return new Refusal(UNRECOGNIZED);
        }
        if (first.name !== outerName) {
            return new Refusal(first.name === 'Min' || first.name === 'Max' ?
                `${first.name}() nested inside ${outerName}() flips direction — expand it manually.`
                : 'Only Min() and Max() composites can be expanded.');
        }
        if (!RatingExpansionEngine.isParen(second, '(')) {
            return new Refusal(UNRECOGNIZED);
        }
        let depth = 0;
        let closeIndex = -1;
        for (const [index, token] of tokens.entries()) {
            if (RatingExpansionEngine.isParen(token, '(')) {
                depth += 1;
            }
            if (RatingExpansionEngine.isParen(token, ')')) {
                depth -= 1;
                if (depth === 0) {
                    closeIndex = index;
                    break;
                }
            }
        }
        if (closeIndex === -1) {
            return new Refusal(UNRECOGNIZED);
        }
        const trailing = tokens.slice(closeIndex + 1);
        let offset = null;
        if (trailing.length > 0) {
            const [operator, number, unit] = trailing;
            const shapeIsOffset = operator?.kind === 'operator' &&
                (operator.symbol === '+' || operator.symbol === '−') &&
                number?.kind === 'number' &&
                trailing.length <= 3 &&
                (unit === undefined || unit.kind === 'unit');
            if (!shapeIsOffset) {
                return new Refusal(UNRECOGNIZED);
            }
            const magnitude = RatingExpansionEngine.toBaseUnit(number, unit?.kind === 'unit' ? unit : undefined, characteristic);
            if (magnitude instanceof Refusal) {
                return magnitude;
            }
            offset = operator.symbol === '−' ? -magnitude : magnitude;
        }
        const terms = [];
        for (const inner of RatingExpansionEngine.splitTopLevelArgs(tokens.slice(2, closeIndex))) {
            const parsed = RatingExpansionEngine.parseArgument(inner, outerName, characteristic);
            if (parsed instanceof Refusal) {
                return parsed;
            }
            for (const term of parsed) {
                if (offset === null) {
                    terms.push(term);
                    continue;
                }
                if (term.signalId === null || term.value !== 0) {
                    return new Refusal("Offsets can't be combined across nesting levels — expand this composite manually.");
                }
                terms.push({ value: offset, signalId: term.signalId });
            }
        }
        return terms;
    }
    static parseTerm(tokens, characteristic) {
        const [first, second, third, fourth] = tokens;
        if (first?.kind === 'number' && tokens.length <= 2) {
            if (second !== undefined && second.kind !== 'unit') {
                return null;
            }
            const value = RatingExpansionEngine.toBaseUnit(first, second, characteristic);
            return value instanceof Refusal ? value : { value, signalId: null };
        }
        if (first?.kind !== 'signal') {
            return null;
        }
        if (characteristic !== sigma_engine_shared_1.BoundaryCharacteristic.Voltage) {
            return new Refusal(`A ${characteristic} rating is a plain number — it can't be measured relative to "${first.signal.name}".`);
        }
        const signalId = String(first.signal.id);
        if (tokens.length === 1) {
            return { value: 0, signalId };
        }
        if (second?.kind !== 'operator' || (second.symbol !== '+' && second.symbol !== '−') || third?.kind !== 'number') {
            return null;
        }
        if (tokens.length > 4 || (fourth !== undefined && fourth.kind !== 'unit')) {
            return null;
        }
        const magnitude = RatingExpansionEngine.toBaseUnit(third, fourth, characteristic);
        if (magnitude instanceof Refusal) {
            return magnitude;
        }
        return { value: second.symbol === '−' ? -magnitude : magnitude, signalId };
    }
    static toBaseUnit(number, unit, characteristic) {
        const declaration = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic];
        if (unit !== undefined && !unit.symbol.endsWith(declaration.short)) {
            return new Refusal(`"${unit.symbol}" isn't a unit of ${characteristic}.`);
        }
        const text = `${number.text}${unit?.symbol ?? ''}`;
        try {
            return (0, sigma_engine_shared_1.parseNumericalSIValue)(text, declaration);
        }
        catch {
            return new Refusal(`"${text}" couldn't be read as a number of ${declaration.long}.`);
        }
    }
    static foldSigns(tokens) {
        const folded = [];
        for (const [index, token] of tokens.entries()) {
            if ((0, expression_tokens_1.isSignOperator)(tokens, index)) {
                continue;
            }
            const previous = tokens[index - 1];
            if (token.kind === 'number' && previous?.kind === 'operator' && (0, expression_tokens_1.isSignOperator)(tokens, index - 1)) {
                folded.push({ kind: 'number', text: `${previous.symbol === '−' ? '-' : ''}${token.text}` });
                continue;
            }
            folded.push(token);
        }
        return folded;
    }
    static correctReversedRange(expanded) {
        const min = expanded.find((slot) => slot.slot === 'min');
        const max = expanded.find((slot) => slot.slot === 'max');
        if (min === undefined || max === undefined || !min.plain || !max.plain) {
            return;
        }
        const [minTerm] = min.terms;
        const [maxTerm] = max.terms;
        if (minTerm === undefined || maxTerm === undefined || minTerm.value <= maxTerm.value) {
            return;
        }
        min.terms = [maxTerm];
        max.terms = [minTerm];
    }
    static splitTopLevelArgs(tokens) {
        const args = [];
        let current = [];
        let depth = 0;
        for (const token of tokens) {
            if (RatingExpansionEngine.isParen(token, '(')) {
                depth += 1;
            }
            if (RatingExpansionEngine.isParen(token, ')')) {
                depth -= 1;
            }
            if (token.kind === 'separator' && depth === 0) {
                args.push(current);
                current = [];
                continue;
            }
            current.push(token);
        }
        args.push(current);
        return args;
    }
    static isParen(token, side) {
        return token?.kind === 'operator' && token.symbol === side;
    }
}
exports.RatingExpansionEngine = RatingExpansionEngine;
//# sourceMappingURL=rating-expansion-engine.js.map